import {TariffPeriodsRecord} from '../../../global/api';
import {PeriodKey} from '../types/client-tariffs.types';

export const formatPrice = (value?: number): string => {
  if (!value) return '0 ₽';
  return `${new Intl.NumberFormat('ru-RU').format(value)} ₽`;
};

export const formatStorage = (value?: number): string => {
  if (!value) return '0 ГБ архива данных';
  const gb = Math.round(value / 1024 / 1024 / 1024);
  return `${gb} ГБ архива данных`;
};

export const getPeriodDescription = (period: PeriodKey): string => {
  switch (period) {
    case 'halfYear':
      return 'Срок действия тарифа - 6 месяцев';
    case 'year':
      return 'Срок действия тарифа - 1 год';
    default:
      return 'Срок действия тарифа - 1 месяц';
  }
};

export const getPeriodTitle = (period: PeriodKey): string => {
  switch (period) {
    case 'halfYear':
      return '6 месяцев';
    case 'year':
      return '1 год';
    default:
      return '1 месяц';
  }
};

export const PERIOD_CODES: Record<PeriodKey, string[]> = {
  month: ['1M', 'MONTH', 'P1M'],
  halfYear: ['6M', 'P6M', 'HALF_YEAR'],
  year: ['12M', '1Y', 'P1Y', 'YEAR'],
};

const getDurationPriceByCodes = (durations: TariffPeriodsRecord[] = [], periodCodes: string[]) => {
  const normalizedCodes = periodCodes.map((code) => code.toUpperCase());
  const duration = durations.find((item) => normalizedCodes.includes((item.period || '').toUpperCase()));
  return duration?.price;
};

export const getDurationByPeriod = (
  period: PeriodKey,
  durations: TariffPeriodsRecord[] = [],
): TariffPeriodsRecord | undefined => {
  const normalizedCodes = PERIOD_CODES[period].map((code) => code.toUpperCase());
  return durations.find((item) => normalizedCodes.includes((item.period || '').toUpperCase()));
};

export const getSaveHint = (period: PeriodKey, durations: TariffPeriodsRecord[] = []): string | null => {
  const monthPrice = getDurationPriceByCodes(durations, PERIOD_CODES.month);
  const selectedPeriodPrice = getDurationPriceByCodes(durations, PERIOD_CODES[period]);

  if (typeof monthPrice !== 'number' || typeof selectedPeriodPrice !== 'number') {
    return 'без скидки за период';
  }

  const diffAmount = (monthPrice - selectedPeriodPrice) * 12;
  if (diffAmount < 0) return null;
  if (!diffAmount) return 'без скидки за период';

  const formattedAmount = new Intl.NumberFormat('ru-RU').format(Math.abs(diffAmount));
  return `сэкономьте ${formattedAmount}р.`;
};

export const getPriceByPeriod = (period: PeriodKey, durations: TariffPeriodsRecord[] = []): number | undefined => {
  const price = getDurationPriceByCodes(durations, PERIOD_CODES[period]);
  return typeof price === 'number' ? price : undefined;
};

export const getCanonicalPeriodCode = (period?: string | null): string => {
  const normalizedPeriod = (period || '').toUpperCase();
  if (PERIOD_CODES.month.includes(normalizedPeriod)) return '1M';
  if (PERIOD_CODES.halfYear.includes(normalizedPeriod)) return '6M';
  if (PERIOD_CODES.year.includes(normalizedPeriod)) return '1Y';
  return normalizedPeriod;
};

export const getPeriodCodeByKey = (period: PeriodKey): '1M' | '6M' | '1Y' => {
  if (period === 'month') return '1M';
  if (period === 'halfYear') return '6M';
  return '1Y';
};

export const getFeaturesFromDescription = (description?: string): string[] =>
  (description || '')
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);

export const normalizeTariffTitle = (title?: string | null): string => (title || '').trim().toLowerCase();
